import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Send, ExternalLink, ArrowRight, Settings2 } from 'lucide-react';
import { toast } from 'sonner';

const STORAGE_KEY = 'lumina_billing_preference';

const getPreference = () => localStorage.getItem(STORAGE_KEY) || 'always_ask';
const setPreferenceValue = (val) => localStorage.setItem(STORAGE_KEY, val);

const SendToBillingDialog = ({ open, onOpenChange, context }) => {
  const navigate = useNavigate();
  const [showPreferences, setShowPreferences] = useState(false);
  const [preference, setPreference] = useState(getPreference);

  useEffect(() => {
    if (!open) return;
    const pref = getPreference();
    setPreference(pref);

    if (pref === 'always_go') {
      onOpenChange(false);
      toast.success('Sent to revQ Billing', { description: context || 'Coding & claim data sent to the billing team.' });
      navigate('/revq/claims');
    } else if (pref === 'always_stay') {
      onOpenChange(false);
      toast.success('Sent to revQ Billing', { description: context || 'Coding & claim data sent. You can review in revQ anytime.' });
    }
  }, [open, navigate, onOpenChange, context]);

  const handleGoNow = () => {
    onOpenChange(false);
    toast.success('Sent to revQ Billing', { description: context || 'Opening revQ Claims...' });
    navigate('/revq/claims');
  };

  const handleStayHere = () => {
    onOpenChange(false);
    toast.success('Sent to revQ Billing', { description: context || 'Data sent. You can review in revQ anytime.' });
  };

  const handleSavePreference = (val) => {
    setPreference(val);
    setPreferenceValue(val);
    toast.success('Preference saved', { description: val === 'always_ask' ? 'You\'ll be asked each time' : val === 'always_go' ? 'Will auto-open revQ next time' : 'Will stay in Lumina next time' });
    setShowPreferences(false);
  };

  // If preference is auto, the dialog won't render the content (handled by useEffect)
  if (getPreference() !== 'always_ask') return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="send-to-billing-dialog">
        <div className="bg-black p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Send className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-white">Sent to Billing</DialogTitle>
              <p className="text-xs text-slate-400 mt-0.5">Claim data has been sent to revQ</p>
            </div>
          </div>
          <DialogDescription className="sr-only">Choose to navigate to revQ or stay in Lumina</DialogDescription>
        </div>

        {!showPreferences ? (
          <div className="p-5 space-y-4">
            <p className="text-sm text-slate-600">Would you like to open revQ to review the claim now?</p>

            <div className="flex flex-col gap-2">
              <Button onClick={handleGoNow} className="bg-black hover:bg-gray-800 gap-2 justify-start h-12" data-testid="billing-go-now-btn">
                <ExternalLink className="h-4 w-4" />
                <div className="text-left">
                  <p className="text-sm font-medium">Open revQ now</p>
                  <p className="text-[10px] text-white/60">Review and submit the claim in revQ</p>
                </div>
                <ArrowRight className="h-4 w-4 ml-auto" />
              </Button>

              <Button variant="outline" onClick={handleStayHere} className="gap-2 justify-start h-12" data-testid="billing-stay-btn">
                <Send className="h-4 w-4 text-slate-500" />
                <div className="text-left">
                  <p className="text-sm font-medium">Stay in Lumina</p>
                  <p className="text-[10px] text-muted-foreground">Continue documenting, review billing later</p>
                </div>
              </Button>
            </div>

            <button onClick={() => setShowPreferences(true)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-slate-700 transition-colors mx-auto" data-testid="billing-preferences-link">
              <Settings2 className="h-3 w-3" /> Set default behavior
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <p className="text-sm font-semibold text-slate-800">Billing Navigation Preference</p>
            <p className="text-xs text-muted-foreground">Choose what happens when you send to billing</p>

            <RadioGroup value={preference} onValueChange={setPreference} className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors" data-testid="pref-always-ask">
                <RadioGroupItem value="always_ask" id="always_ask" />
                <div>
                  <Label htmlFor="always_ask" className="text-sm font-medium cursor-pointer">Always ask me</Label>
                  <p className="text-[11px] text-muted-foreground">Show this dialog every time</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors" data-testid="pref-always-go">
                <RadioGroupItem value="always_go" id="always_go" />
                <div>
                  <Label htmlFor="always_go" className="text-sm font-medium cursor-pointer">Always open revQ</Label>
                  <p className="text-[11px] text-muted-foreground">Automatically navigate to revQ Claims</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors" data-testid="pref-always-stay">
                <RadioGroupItem value="always_stay" id="always_stay" />
                <div>
                  <Label htmlFor="always_stay" className="text-sm font-medium cursor-pointer">Always stay in Lumina</Label>
                  <p className="text-[11px] text-muted-foreground">Send silently, no navigation</p>
                </div>
              </label>
            </RadioGroup>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowPreferences(false)} data-testid="pref-cancel-btn">Cancel</Button>
              <Button className="flex-1 bg-black hover:bg-gray-800" onClick={() => handleSavePreference(preference)} data-testid="pref-save-btn">Save Preference</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { getPreference, setPreferenceValue, STORAGE_KEY };
export default SendToBillingDialog;
